/**
 * A React functional component that renders a dropdown select input for selecting a timezone.
 * It integrates with Redux for state management and supports validation and error handling.
 *
 * @component
 * @param {Object} props - The props for the component.
 * @param {any} props.setting - The current setting object to be displayed.
 * @param {any[]} props.newSettings - The list of all settings, used for validation and error tracking.
 * @param {Function} props.onInputChange - Callback function triggered when the input value changes.
 * @param {string} props.productId - The ID of the product associated with the setting.
 * @param {any} props.settingKeyValidation - Validation logic for the setting key.
 *
 * @returns {JSX.Element} The rendered SelectComponent.
 */
import {
  Control,
  Label,
  Select,
  SelectOption,
  SelectOptionSearch,
  ControlError
} from "@forge/common";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { tenantActions } from "../../../store/Tenant/tenant-slice";
import "./SelectComponent.scss";
import { AppDispatch } from "../../../store";
import staticContent from "../../../utils/staticContent.json";

interface SelectComponentProps {
  setting: any;
  newSettings: any[];
  onInputChange: (
    event: any,
    setting: any,
    productId: string,
    errorMessage: string
  ) => void;
  productId: string;
  settingKeyValidation: any;
}

interface SelectOptionItem {
  id: string;
  name: string;
}

interface SearchEvent {
  target: {
    value: string;
  };
}

const SelectComponent: React.FC<SelectComponentProps> = ({
  setting,
  newSettings,
  onInputChange,
  productId,
  settingKeyValidation,
}) => {
  const [optionList, setOptionList] = useState<SelectOptionItem[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<SelectOptionItem[]>([]);
  const errorMessage = useRef(
    newSettings?.find((x: { key: any }) => x.key === setting.key)?.errorMessage
  );
  const [selectedOptionValue, setSelectedOptionValue] = useState(
    newSettings?.find((x: { key: any }) => x.key === setting.key)?.value ??
      setting?.defaultValue ??
      ""
  );
  const [selectedOptionId, setSelectedOptionId] = useState<string>("");
  const isRequired = setting?.required === true;
  const selectId = `product-setting-select-${setting.key}`;
  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    const metadataOptions = (Array.isArray(setting?.values) ? setting.values : []).map((option: any) => ({
      id: String(option?.id ?? option?.value ?? option?.name ?? ""),
      name: String(option?.name ?? option?.id ?? option?.value ?? ""),
    }));

    setOptionList(metadataOptions);
    setFilteredOptions(metadataOptions);

    const defaultOption = metadataOptions.find(
      (option: SelectOptionItem) =>
        option.name === selectedOptionValue || option.id === selectedOptionValue
    );

    setSelectedOptionId(defaultOption?.id ?? "");
  }, [selectedOptionValue, setting?.values]);

  /**
   *
   * @param event
   * * Handles the change event for the select input.
   * * It validates the selected value and updates the error message accordingly.
   * * It also calculates the total error count for the settings and dispatches it to the Redux store.
   */
  const handleChange = (event: any) => {
    const normalizedEvent = {
      target: {
        value: event?.target?.value ?? "",
      },
    };

    errorMessage.current =
      !isRequired && normalizedEvent.target.value === ""
        ? ""
        : settingKeyValidation.onChange(normalizedEvent);

    const errorCount = newSettings.reduce((count, otherSetting) => {
      if (!otherSetting) {
        return count;
      }

      if (otherSetting.key === setting.key) {
        return errorMessage.current?.length > 0 ? count + 1 : count;
      }
      return otherSetting?.errorMessage?.length > 0 ? count + 1 : count;
    }, 0);

    dispatch(
      tenantActions.updateTabErrorCountMapping({
        tabName: productId,
        errorCount,
      } as any)
    );
  };

  /**
   *
   * @param event user input
   * * Handles the search event for filtering the timezone list based on user input.
   * * It updates the filtered timezone list based on the search value.
   */
  const handleSearch = (event: SearchEvent): void => {
    const searchValue = event.target.value.toLowerCase();
    const filteredOptionList = optionList.filter((option: SelectOptionItem) =>
      option.name.toLowerCase().includes(searchValue)
    );

    setFilteredOptions(filteredOptionList);
  };

  return (
    <Control className="control-select-component">
      <Label htmlFor={selectId} required={isRequired}>
        {setting.displayName}
      </Label>
      <Select
        aria-label={setting.displayName}
        className="select-component"
        height={2}
        id={selectId}
        onChange={(event) => {
          const optionValue = event.target.value;
          const selectedOption = filteredOptions.find(
            (option: any) =>
              String(option.id ?? option.value ?? option.name) ===
              String(optionValue)
          );

          const selectedOptionName =
            selectedOption?.name ??
            selectedOption?.id ??
            optionValue;

          setSelectedOptionId(optionValue);

          const newEvent = {
            target: {
              value: selectedOptionName,
            },
          };

          handleChange(newEvent);
          onInputChange(newEvent, setting, productId, errorMessage.current);
        }}
        placeholder={staticContent.select}
        size="regular"
        value={selectedOptionId}
        search={
          <SelectOptionSearch
            label={staticContent.search}
            onChange={handleSearch}
            placeholder={staticContent.search}
            className="select-option-search"
            data-testid="select-option-search"
          />
        }
        data-testid="select-component"
      >
        {filteredOptions?.map((option: any) => {
          const optionId = option.id ?? option.value ?? option.name;
          const optionLabel = option.name ?? optionId;

          return (
            <SelectOption
              key={optionId}
              value={optionId}
              label={optionLabel}
              data-testid="select-option"
            />
          );
        })}
      </Select>
      {errorMessage.current?.length > 0 && (
        <ControlError className="requiredText">
          {errorMessage.current}
        </ControlError>
      )}
    </Control>
  );
};

export default SelectComponent;
